import React, { useState, useMemo } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';

const DateRangeSelector = ({ 
  babyBook, 
  entries, 
  dateRange, 
  onDateRangeChange 
}) => {
  const [mode, setMode] = useState('preset'); // 'preset' or 'custom'

  // Calculate baby's age periods based on birth date
  const presets = useMemo(() => {
    if (!babyBook?.birthDate) return [];
    
    const birthDate = babyBook.birthDate?.toDate 
      ? babyBook.birthDate.toDate() 
      : new Date(babyBook.birthDate);
    
    const now = new Date();
    const presetOptions = [];

    // All time
    presetOptions.push({
      label: 'All Entries',
      description: 'Everything in this baby book',
      start: birthDate,
      end: now
    });

    // Year 1
    const year1End = new Date(birthDate);
    year1End.setFullYear(year1End.getFullYear() + 1);
    if (birthDate <= now) {
      presetOptions.push({
        label: 'Year 1',
        description: 'First 12 months',
        start: birthDate,
        end: year1End > now ? now : year1End
      });
    }

    // Year 2
    const year2Start = new Date(birthDate);
    year2Start.setFullYear(year2Start.getFullYear() + 1);
    const year2End = new Date(birthDate);
    year2End.setFullYear(year2End.getFullYear() + 2);
    if (year2Start <= now) {
      presetOptions.push({
        label: 'Year 2',
        description: '12-24 months',
        start: year2Start,
        end: year2End > now ? now : year2End
      });
    }

    // First 6 months
    const sixMonthsEnd = new Date(birthDate);
    sixMonthsEnd.setMonth(sixMonthsEnd.getMonth() + 6);
    presetOptions.push({
      label: 'First 6 Months',
      description: 'Newborn period',
      start: birthDate,
      end: sixMonthsEnd > now ? now : sixMonthsEnd
    });

    // Months 6-12
    const sixMonthsStart = new Date(birthDate);
    sixMonthsStart.setMonth(sixMonthsStart.getMonth() + 6);
    const twelveMonthsEnd = new Date(birthDate);
    twelveMonthsEnd.setFullYear(twelveMonthsEnd.getFullYear() + 1);
    if (sixMonthsStart <= now) {
      presetOptions.push({
        label: 'Months 6-12',
        description: 'Second half of year 1',
        start: sixMonthsStart,
        end: twelveMonthsEnd > now ? now : twelveMonthsEnd
      });
    }

    // Last 3 months
    const threeMonthsAgo = new Date(now);
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
    if (threeMonthsAgo >= birthDate) {
      presetOptions.push({
        label: 'Last 3 Months',
        description: 'Recent memories',
        start: threeMonthsAgo,
        end: now
      });
    }

    return presetOptions;
  }, [babyBook?.birthDate]);

  // Count entries in each preset range
  const getEntryCount = (start, end) => {
    if (!entries) return 0;
    return entries.filter(entry => {
      const entryDate = entry.timestamp?.toDate 
        ? entry.timestamp.toDate() 
        : new Date(entry.timestamp);
      return entryDate >= start && entryDate <= end;
    }).length;
  };

  const handlePresetSelect = (preset) => {
    onDateRangeChange({
      start: preset.start,
      end: preset.end,
      label: preset.label
    });
  };

  const handleCustomChange = (field, value) => {
    onDateRangeChange({
      ...dateRange,
      [field]: new Date(value),
      label: 'Custom Range'
    });
  };

  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = date?.toDate ? date.toDate() : new Date(date);
    return d.toISOString().split('T')[0];
  };

  const isPresetSelected = (preset) => {
    if (!dateRange?.start || !dateRange?.end) return false;
    const startMatch = formatDateForInput(dateRange.start) === formatDateForInput(preset.start);
    const endMatch = formatDateForInput(dateRange.end) === formatDateForInput(preset.end);
    return startMatch && endMatch;
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-ocean-900 flex items-center gap-2">
        <Calendar className="w-5 h-5" />
        Select Time Period
      </h3>

      {/* Mode toggle */}
      <div className="flex gap-2">
        <button
          onClick={() => setMode('preset')}
          className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
            mode === 'preset'
              ? 'bg-ocean-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Quick Select
        </button>
        <button
          onClick={() => setMode('custom')}
          className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
            mode === 'custom'
              ? 'bg-ocean-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Custom Dates
        </button>
      </div>

      {mode === 'preset' ? (
        <div className="grid grid-cols-2 gap-2">
          {presets.map((preset, idx) => {
            const entryCount = getEntryCount(preset.start, preset.end);
            const isSelected = isPresetSelected(preset);
            
            return (
              <button
                key={idx}
                onClick={() => handlePresetSelect(preset)}
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  isSelected
                    ? 'border-ocean-500 bg-ocean-50'
                    : 'border-gray-200 hover:border-ocean-300 bg-white'
                }`}
              >
                <p className={`font-semibold text-sm ${isSelected ? 'text-ocean-900' : 'text-gray-900'}`}>
                  {preset.label}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">{preset.description}</p>
                <p className={`text-xs mt-1 ${isSelected ? 'text-ocean-600' : 'text-gray-400'}`}>
                  {entryCount} entries
                </p>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={formatDateForInput(dateRange?.start)}
              onChange={(e) => handleCustomChange('start', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={formatDateForInput(dateRange?.end)}
              onChange={(e) => handleCustomChange('end', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-ocean-500"
            />
          </div>
          
          {dateRange?.start && dateRange?.end && (
            <p className="text-sm text-ocean-600 text-center pt-2">
              {getEntryCount(dateRange.start, dateRange.end)} entries in selected range
            </p>
          )}
        </div>
      )}

      {/* Current selection summary */}
      {dateRange?.start && dateRange?.end && (
        <div className="bg-ocean-50 border border-ocean-200 rounded-lg p-3 text-center">
          <p className="text-sm text-ocean-800">
            <span className="font-semibold">{dateRange.label || 'Selected'}:</span>{' '}
            {new Date(dateRange.start).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            {' — '}
            {new Date(dateRange.end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
      )}
    </div>
  );
};

export default DateRangeSelector;